/**
 * Перевод сырых Suno-ошибок в понятные русские сообщения.
 *
 * Suno API отдаёт ошибки на английском техническим языком — юзер не понимает
 * что делать. Здесь централизованный перевод наиболее частых кейсов.
 *
 * Принципы:
 *   - Переводим ТОЛЬКО те ошибки где уверены в смысле (по ключевым фразам)
 *   - Общие слова типа «not found» сами по себе НИ О ЧЁМ не говорят — не
 *     надо додумывать что «трек удалён»: это может быть что угодно
 *   - Если не распознали — возвращаем оригинал (пусть юзер хотя бы видит
 *     полную ошибку для поддержки) + логируем в консоль
 *
 * Используется везде где мы ловим error от convertToWav/generateCover/etc.
 */

/** Контекст операции — для более точного перевода неоднозначных ошибок.
 *  Например "record does not exist" в контексте MIDI имеет специфичный смысл. */
export type SunoOperationContext =
	| 'midi'
	| 'wav'
	| 'cover'
	| 'video'
	| 'persona'
	| 'timestamped'
	| 'stems'
	| 'generate'
	| 'extend'

export function translateSunoError(
	raw: string | null | undefined,
	operation?: SunoOperationContext,
): string {
	if (!raw) return 'Неизвестная ошибка'
	const text = raw.trim()
	const low = text.toLowerCase()

	// Всегда логируем raw в консоль — помогает в саппорт-тикетах понять что
	// реально ответило API. Translate — для UI, лог — для разработки.
	if (typeof console !== 'undefined' && console.warn) {
		console.warn('[Suno error raw]', text)
	}

	// ── MIDI / stems prerequisite ────────────────────────────────
	if (
		low.includes('not separated') ||
		low.includes('need stem') ||
		low.includes('need separat') ||
		low.includes('stems required') ||
		low.includes('must separate')
	) {
		return 'Для этого действия сначала нужно разделить трек на стемы (WAV-track и MIDI требуют отдельной дорожки вокала/инструментала).'
	}

	// ── Already-exists варианты ──────────────────────────────────
	if (low.includes('wav record already exists')) {
		return 'WAV уже сконвертирован. Обнови страницу — если не появилась кнопка «Скачать WAV», попробуй ещё раз.'
	}
	if (low.includes('already exists') || low.includes('already been generated') || low.includes('already generated')) {
		return 'Эта операция уже выполнялась для трека. Обнови страницу — результат должен подтянуться.'
	}

	// ── Prompt требуется ─────────────────────────────────────────
	if (low.includes('prompt cannot be empty')) {
		return 'У этого трека не указан промпт, поэтому Suno не может выполнить операцию. Для обложки — загрузи свою картинку через «Загрузить свою обложку».'
	}

	// ── Другие обязательные поля ─────────────────────────────────
	if (
		low.includes('style cannot be blank') || low.includes('style is required') ||
		low.includes('tags cannot be blank') || low.includes('tags is required')
	) {
		return 'Не указан стиль трека. Добавь жанр или описание звучания в поле «Стиль».'
	}
	if (low.includes('lyrics cannot be blank') || low.includes('lyrics is required') || low.includes('lyric is required')) {
		return 'Не указан текст песни. Напиши его сам, либо включи «Инструментал», либо выключи Custom-режим (тогда Suno напишет текст сам).'
	}
	if (low.includes('title cannot be blank') || low.includes('title is required')) {
		return 'Не указано название трека. Добавь короткое название (или выключи Custom-режим).'
	}
	// Replace Section: провайдер не получил границы фрагмента (поля
	// infillStartS/infillEndS). После перехода Suno на канонический нейминг
	// встречается только на необновлённом бэке.
	if (low.includes('infill_start_s') || low.includes('infill_end_s') || low.includes('infillstart') || low.includes('infillend')) {
		return 'Suno не получил границы фрагмента. Выдели фрагмент заново и попробуй ещё раз.'
	}
	if (low.includes('task_id cannot be blank') || low.includes('audio_id cannot be blank') || low.includes('taskid cannot be blank') || low.includes('audioid cannot be blank')) {
		return 'Ошибка в данных запроса — нужны taskId/audioId трека. Попробуй ещё раз или выбери другой трек.'
	}

	// ── Persona специфичное ──────────────────────────────────────
	if (low.includes('persona') && (low.includes('invalid') || low.includes('not found') || low.includes('not exist'))) {
		return 'Выбранная персона больше недоступна в Suno (возможно, была удалена). Выбери другую или «Автоматический голос».'
	}

	// ── NSFW / content safety ────────────────────────────────────
	if (
		low.includes('nsfw') ||
		low.includes('content safety') ||
		low.includes('content policy') ||
		low.includes('inappropriate content') ||
		low.includes('violates')
	) {
		return 'Suno отклонил запрос из-за политики контента. Переформулируй промпт или текст песни без запрещённых тем (насилие, эротика, политика).'
	}

	// ── Параллельная генерация / очередь ─────────────────────────
	if (low.includes('wait for current') || low.includes('generation in progress') || low.includes('please wait')) {
		return 'У тебя уже идёт генерация. Дождись её завершения и попробуй снова.'
	}

	// ── Имя исполнителя в стиле/тегах (владелец 2026-08-21) ───────
	// «Your tags contain artist name manowar - we don't reference specific
	// artists»: показываем имя и что делать — иначе юзер лупит повторы.
	if (low.includes('artist name') || low.includes("don't reference specific artists")) {
		const m = text.match(/artist name\s+(.+?)\s*[-–—]\s*we\s+do(?:n['’]t| not)\s+reference/i)
		const name = m?.[1]?.trim()
		return `Suno не принимает имена исполнителей в стиле и тегах${name ? ` («${name}»)` : ''}. Убери имя и опиши звучание своими словами — жанр, настроение, инструменты, вокал (например: «эпичный хеви-метал, мощный мужской вокал, гитарные риффы»).`
	}

	// ── Copyright / catalog match ───────────────────────────────
	if (low.includes('the lyrics are empty')) {
		return 'Mureka поёт только присланный текст — впиши слова песни и запусти снова.'
	}

	if (low.includes('at least one of the prompt and styles')) {
		return 'Mureka требует указать стиль трека. Впиши стиль (например «rock, энергичный») и запусти снова.'
	}

	if (low.includes('instrumental_id not found')) {
		return 'Mureka не приняла референс для инструментала. Загрузи файл заново и повтори.'
	}

	if (low.includes('matches an existing recording') || low.includes('existing recording in our catalog')) {
		return 'Suno распознал этот трек как существующую запись из своего каталога и отклонил обработку (защита авторских прав). Известные песни он не принимает — попробуй другой файл или сделай это через Mureka («Создать по референсу» / «Cover»): она обрабатывает без каталожной проверки.'
	}

	// ── Не удалось скачать source-аудио ──────────────────────────
	if (low.includes('failed to fetch audio') || low.includes('cannot download audio') || low.includes('audio unavailable')) {
		return 'Suno не смог загрузить исходный аудио-файл. Попробуй ещё раз или используй другой трек.'
	}

	// ── Трек именно НЕ НАЙДЕН (уверенный маркер) ─────────────────
	// Только чётко «track/audio not found» — тут Suno реально потерял запись.
	if (
		low.includes('track not found') ||
		low.includes('source track not found') ||
		low.includes('audio not found') ||
		low.includes('source audio not found')
	) {
		return (
			'Suno потерял метаданные этого трека на своей стороне (такое бывает ' +
			'со старыми треками или при переполнении их хранилища). MP3 и обложка ' +
			'у тебя остаются — их можно слушать и скачивать. Но новые операции ' +
			'(WAV, обложка, MIDI, продление и т.п.) для этого трека Suno выполнить ' +
			'не сможет. Чтобы получить эти возможности — сгенерируй трек заново ' +
			'с тем же промптом.'
		)
	}

	// ── Record не найден для конкретной операции ─────────────────
	// «record does not exist» / invalid taskId/audioId — это НЕ про трек
	// в целом, а про то что у конкретной операции нет записи на стороне
	// Suno. Текст зависит от operation — разные операции имеют разные
	// типовые причины этой ошибки.
	if (
		low.includes('record does not exist') ||
		(low.includes('taskid') && (low.includes('invalid') || low.includes('not exist'))) ||
		(low.includes('audioid') && (low.includes('invalid') || low.includes('not exist')))
	) {
		if (operation === 'midi') {
			return (
				'Suno не смог построить MIDI для этого трека. MIDI работает не для ' +
				'всех песен — нужна чёткая структура нот (обычно получается на pop/' +
				'electronic/classical, хуже — на шансоне/rap/lo-fi). Попробуй:\n' +
				'• на другом треке — желательно с чистым инструменталом;\n' +
				'• через стемы: сначала «Стемы» (вокал + инструментал), MIDI затем ' +
				'может получиться на инструментале.\n' +
				'Остальные операции (WAV, видео, обложка) на этом треке доступны.'
			)
		}
		if (operation === 'timestamped') {
			return (
				'Suno не смог привязать таймкоды к этому тексту. Такое бывает когда ' +
				'вокал плохо разделяется или трек инструментальный. Попробуй другой ' +
				'трек с чёткими вокальными партиями.'
			)
		}
		if (operation === 'video') {
			return (
				'Suno не смог создать видеоклип для этого трека. Попробуй через пару ' +
				'минут, либо на другом треке.'
			)
		}
		if (operation === 'persona') {
			return (
				'Suno не смог создать персону из этого трека. Персоны лучше получаются ' +
				'из треков с чистым вокалом — попробуй другой трек.'
			)
		}
		return (
			'Suno отказался выполнять эту операцию для данного трека. Попробуй:\n' +
			'• Повторить через минуту — часто это временная ошибка на их стороне.\n' +
			'• Открыть оригинальный трек (из «Мои треки»), а не запись обложки ' +
			'или текст песни — служебные операции работают только с полноценными ' +
			'треками.\n' +
			'• Если повторяется — пришли скрин в саппорт.'
		)
	}

	// ── Квоты / баланс на стороне Suno ───────────────────────────
	if (low.includes('insufficient') || low.includes('no credit') || low.includes('quota')) {
		return 'Недостаточно токенов или превышена квота. Пополни баланс или попробуй позже.'
	}

	// ── Лимиты длительности ──────────────────────────────────────
	if (low.includes('too long') || low.includes('exceed') || low.includes('max duration')) {
		return 'Трек слишком длинный для этой операции. Попробуй на более коротком фрагменте.'
	}

	// ── Модель/версия ────────────────────────────────────────────
	if (low.includes('model') && (low.includes('not support') || low.includes('unsupported'))) {
		return 'Эта операция недоступна для выбранной модели Suno. Попробуй на треке другой модели.'
	}

	// ── Timeouts / network ───────────────────────────────────────
	if (low.includes('timeout') || low.includes('timed out')) {
		return 'Превышено время ожидания. Попробуй позже — очередь Suno может быть перегружена.'
	}

	// ── Rate limiting ────────────────────────────────────────────
	if (low.includes('rate limit') || low.includes('too many requests') || low.includes('429')) {
		return 'Слишком много запросов к Suno. Подожди 10-20 секунд и попробуй снова.'
	}

	// ── Nothing matched — отдаём оригинал с hint'ом ──────────────
	// Юзеру показываем raw-ошибку и намёк что это техническая. Провайдера НЕ
	// называем: эта же функция переводит и ошибки Mureka — раньше «Ошибка Suno:»
	// вводила в заблуждение (Mureka-ошибку подписывали как Suno).
	return `Ошибка: ${text}`
}
